/**
 * CIW (Customer Information Window) State Persistence
 * Maintains console messages, commands, and session data across navigation
 */

import { isObject } from './safeStorage'

export interface ConsoleMessage {
  id: string
  timestamp: Date
  message: string
  type: 'info' | 'warning' | 'error' | 'command'
}

export interface CIWSessionState {
  messages: ConsoleMessage[]
  commandHistory: string[]
  currentCommand: string
  sessionStartTime: Date
  lastActivity: Date
  preferences: {
    autoScroll: boolean
    maxMessages: number
    showTimestamps: boolean
    theme: 'dark' | 'light'
  }
  version: string
}

const CIW_STORAGE_KEY = 'girasoles_ciw_state'
const CIW_HISTORY_KEY = 'girasoles_ciw_history'
const CIW_VERSION = '1.0.0'
const MAX_MESSAGES = 1000
const MAX_HISTORY = 100

/**
 * Default CIW state
 */
const getDefaultState = (): CIWSessionState => ({
  messages: [
    {
      id: '1',
      timestamp: new Date(),
      message: 'Girasoles CAD Workbench v3.0.0 initialized',
      type: 'info'
    }
  ],
  commandHistory: [],
  currentCommand: '',
  sessionStartTime: new Date(),
  lastActivity: new Date(),
  preferences: {
    autoScroll: true,
    maxMessages: MAX_MESSAGES,
    showTimestamps: true,
    theme: 'dark'
  },
  version: CIW_VERSION
})

/**
 * Serialize messages for storage (convert Date objects to strings)
 */
const serializeState = (state: CIWSessionState): string => {
  const serializable = {
    ...state,
    messages: state.messages.map(msg => ({
      ...msg,
      timestamp: msg.timestamp.toISOString()
    })),
    sessionStartTime: state.sessionStartTime.toISOString(),
    lastActivity: state.lastActivity.toISOString()
  }
  return JSON.stringify(serializable)
}

/**
 * Deserialize messages from storage (convert strings back to Date objects)
 */
const isStoredCIWShape = (v: unknown): v is Record<string, unknown> & { messages: unknown[] } =>
  isObject(v) &&
  Array.isArray((v as Record<string, unknown>).messages) &&
  Array.isArray((v as Record<string, unknown>).commandHistory) &&
  typeof (v as Record<string, unknown>).version === 'string' &&
  isObject((v as Record<string, unknown>).preferences)

const deserializeState = (data: string): CIWSessionState | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(data)
  } catch {
    return null
  }
  if (!isStoredCIWShape(parsed)) {
    return null
  }
  return {
    ...(parsed as unknown as CIWSessionState),
    messages: (parsed.messages as Array<Record<string, unknown>>)
      .filter((msg) => isObject(msg) && typeof msg.id === 'string' && typeof msg.message === 'string')
      .map((msg) => ({
        ...(msg as unknown as ConsoleMessage),
        timestamp: new Date(msg.timestamp as string | number)
      })),
    sessionStartTime: new Date((parsed as Record<string, unknown>).sessionStartTime as string | number),
    lastActivity: new Date((parsed as Record<string, unknown>).lastActivity as string | number)
  }
}

/**
 * Save CIW state to localStorage
 */
export const saveCIWState = (state: CIWSessionState): void => {
  try {
    // Update last activity
    const updatedState = {
      ...state,
      lastActivity: new Date()
    }

    // Trim messages if too many
    if (updatedState.messages.length > MAX_MESSAGES) {
      updatedState.messages = updatedState.messages.slice(-MAX_MESSAGES)
    }

    // Trim command history if too long
    if (updatedState.commandHistory.length > MAX_HISTORY) {
      updatedState.commandHistory = updatedState.commandHistory.slice(-MAX_HISTORY)
    }

    localStorage.setItem(CIW_STORAGE_KEY, serializeState(updatedState))
    // Removed excessive logging: console.log('CIW state saved successfully')
  } catch (error) {
    console.error('Failed to save CIW state:', error)
  }
}

/**
 * Load CIW state from localStorage
 */
export const loadCIWState = (): CIWSessionState => {
  try {
    const saved = localStorage.getItem(CIW_STORAGE_KEY)
    if (!saved) {
      return getDefaultState()
    }

    const state = deserializeState(saved)
    if (!state) {
      localStorage.removeItem(CIW_STORAGE_KEY)
      return getDefaultState()
    }

    if (state.version !== CIW_VERSION) {
      console.warn('CIW state version mismatch, using default state')
      return getDefaultState()
    }

    const now = new Date()
    const sessionAge = now.getTime() - state.sessionStartTime.getTime()
    const maxAge = 24 * 60 * 60 * 1000

    if (sessionAge > maxAge) {
      return getDefaultState()
    }

    return state
  } catch (error) {
    console.error('Failed to load CIW state:', error)
    return getDefaultState()
  }
}

/**
 * Clear CIW state
 */
export const clearCIWState = (): void => {
  try {
    localStorage.removeItem(CIW_STORAGE_KEY)
    localStorage.removeItem(CIW_HISTORY_KEY)
    // Removed excessive logging: console.log('CIW state cleared')
  } catch (error) {
    console.error('Failed to clear CIW state:', error)
  }
}

/**
 * Add a message to the state and persist
 */
export const addMessageToState = (
  state: CIWSessionState,
  message: string,
  type: ConsoleMessage['type'] = 'info'
): CIWSessionState => {
  const newMessage: ConsoleMessage = {
    id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
    timestamp: new Date(),
    message,
    type
  }

  const newState = {
    ...state,
    messages: [...state.messages, newMessage],
    lastActivity: new Date()
  }

  // Auto-save
  saveCIWState(newState)
  return newState
}

/**
 * Add a command to history and persist
 */
export const addCommandToHistory = (
  state: CIWSessionState,
  command: string
): CIWSessionState => {
  // Don't add empty commands or duplicates
  if (!command.trim() || state.commandHistory[state.commandHistory.length - 1] === command) {
    return state
  }

  const newState = {
    ...state,
    commandHistory: [...state.commandHistory, command],
    lastActivity: new Date()
  }

  // Auto-save
  saveCIWState(newState)
  return newState
}

/**
 * Update current command (for persistence across navigation)
 */
export const updateCurrentCommand = (
  state: CIWSessionState,
  command: string
): CIWSessionState => {
  const newState = {
    ...state,
    currentCommand: command,
    lastActivity: new Date()
  }

  // Auto-save with debouncing for current command
  debouncedSave(newState)
  return newState
}

/**
 * Update preferences
 */
export const updatePreferences = (
  state: CIWSessionState,
  preferences: Partial<CIWSessionState['preferences']>
): CIWSessionState => {
  const newState = {
    ...state,
    preferences: { ...state.preferences, ...preferences },
    lastActivity: new Date()
  }

  saveCIWState(newState)
  return newState
}

/**
 * Get command history for autocomplete/suggestions
 */
export const getCommandHistory = (): string[] => {
  try {
    const state = loadCIWState()
    return state.commandHistory
  } catch (error) {
    console.error('Failed to get command history:', error)
    return []
  }
}

/**
 * Search messages by text
 */
export const searchMessages = (
  state: CIWSessionState,
  searchTerm: string
): ConsoleMessage[] => {
  if (!searchTerm.trim()) return state.messages

  const term = searchTerm.toLowerCase()
  return state.messages.filter(msg =>
    msg.message.toLowerCase().includes(term) ||
    msg.type.toLowerCase().includes(term)
  )
}

/**
 * Export messages as text
 */
export const exportMessages = (state: CIWSessionState): string => {
  const header = `Girasoles CAD Workbench Console Log
Session: ${state.sessionStartTime.toISOString()}
Exported: ${new Date().toISOString()}
Messages: ${state.messages.length}
${'='.repeat(50)}

`

  const messages = state.messages.map(msg => {
    const timestamp = msg.timestamp.toISOString()
    const type = msg.type.toUpperCase().padEnd(7)
    return `[${timestamp}] ${type} ${msg.message}`
  }).join('\n')

  return header + messages
}

/**
 * Debounced save for frequent updates like current command
 */
let saveTimeout: NodeJS.Timeout | null = null

const debouncedSave = (state: CIWSessionState, delay: number = 1000): void => {
  if (saveTimeout) {
    clearTimeout(saveTimeout)
  }

  saveTimeout = setTimeout(() => {
    saveCIWState(state)
  }, delay)
}

/**
 * Check if CIW has saved state
 */
export const hasSavedCIWState = (): boolean => {
  try {
    return localStorage.getItem(CIW_STORAGE_KEY) !== null
  } catch (error) {
    return false
  }
}

/**
 * Get session info
 */
export const getSessionInfo = (state: CIWSessionState) => {
  const now = new Date()
  const sessionDuration = now.getTime() - state.sessionStartTime.getTime()
  const lastActivityAge = now.getTime() - state.lastActivity.getTime()

  return {
    sessionDuration: Math.floor(sessionDuration / 1000), // seconds
    lastActivityAge: Math.floor(lastActivityAge / 1000), // seconds
    messageCount: state.messages.length,
    commandCount: state.commandHistory.length,
    isActive: lastActivityAge < 5 * 60 * 1000 // active if last activity < 5 minutes
  }
}
